/**
 * DateTimePicker - Date and time picker component
 */

import DatePicker from "./DatePicker.js";
import TimePicker from "./TimePicker.js";

class DateTimePicker {
  constructor(options = {}) {
    this.options = {
      calendar: "nepali", // 'nepali' or 'english'
      language: "en", // 'en' or 'ne'
      dateFormat: "YYYY-MM-DD", // Date format
      timeFormat: "12", // '12' or '24'
      format: "YYYY-MM-DD HH:mm", // Combined datetime format
      placeholder: "Select date & time", // Input placeholder
      minDate: null, // Minimum selectable date
      maxDate: null, // Maximum selectable date
      minTime: null, // Minimum selectable time
      maxTime: null, // Maximum selectable time
      disabledDates: [], // Array of disabled dates
      disabledDaysOfWeek: [], // Array of disabled weekdays
      minuteStep: 5, // Time minute step
      closeOnSelect: true, // Close picker on selection
      showInput: true, // Show input field
      inline: false, // Show picker inline
      onChange: null, // Callback on change
      onShow: null, // Callback on show
      onHide: null, // Callback on hide
      ...options,
    };

    this.container = null;
    this.input = null;
    this.pickerContainer = null;
    this.datePicker = null;
    this.timePicker = null;
    this.isVisible = false;
    this.initialized = false;
    this.activeTab = "date"; // 'date' or 'time'
  }

  /**
   * Initialize the datetime picker
   * @param {HTMLElement|string} element - Container element or selector
   */
  initialize(element) {
    if (typeof element === "string") {
      element = document.querySelector(element);
    }

    if (!element) {
      throw new Error("Container element not found");
    }

    this.container = element;
    this._createStructure();
    this._initializePickers();
    this._attachEventListeners();
    this.initialized = true;

    if (this.options.inline) {
      this.show();
    }
  }

  /**
 * Create picker structure with size support
 * @private
 */
_createStructure() {
  // Clear container
  this.container.innerHTML = "";
  this.container.classList.add("ndtp-datetime-picker");
  
  // Apply size class
  if (this.options.size) {
    // Remove any existing size classes
    this.container.classList.remove('ndtp-size-sm', 'ndtp-size-md', 'ndtp-size-lg');
    // Add the specified size class
    this.container.classList.add(`ndtp-size-${this.options.size}`);
  }

  // Create input if needed
  if (this.options.showInput) {
    this.input = document.createElement("input");
    this.input.type = "text";
    this.input.className = "ndtp-input";
    this.input.placeholder = this.options.placeholder;
    this.input.readOnly = true;
    this.container.appendChild(this.input);
  }

  // Create picker container
  this.pickerContainer = document.createElement("div");
  this.pickerContainer.className = "ndtp-picker-container";
  if (!this.options.inline) {
    this.pickerContainer.classList.add("ndtp-popup");
    this.pickerContainer.style.display = "none";
  }
  
  // Also apply size class to picker container for consistency
  if (this.options.size) {
    this.pickerContainer.classList.add(`ndtp-size-${this.options.size}`);
  }

  // Create tabs
  const tabs = this._createTabs();
  this.pickerContainer.appendChild(tabs);

  // Create content wrapper
  const contentWrapper = document.createElement("div");
  contentWrapper.className = "ndtp-content-wrapper";

  // Create content areas
  const dateContainer = document.createElement("div");
  dateContainer.className = "ndtp-date-container";
  contentWrapper.appendChild(dateContainer);

  const timeContainer = document.createElement("div");
  timeContainer.className = "ndtp-time-container";
  contentWrapper.appendChild(timeContainer);

  this.pickerContainer.appendChild(contentWrapper);

  // Create footer
  const footer = this._createFooter();
  this.pickerContainer.appendChild(footer);

  this.container.appendChild(this.pickerContainer);

  // Initialize visibility
  this._updateTabVisibility("date");
}

  /**
   * Create tabs
   * @private
   */
  _createTabs() {
    const tabs = document.createElement("div");
    tabs.className = "ndtp-tabs";

    const dateTab = document.createElement("button");
    dateTab.className = "ndtp-tab active";
    dateTab.textContent = "Date";
    dateTab.type = "button";
    dateTab.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._switchTab("date");
    };

    const timeTab = document.createElement("button");
    timeTab.className = "ndtp-tab";
    timeTab.textContent = "Time";
    timeTab.type = "button";
    timeTab.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._switchTab("time");
    };

    tabs.appendChild(dateTab);
    tabs.appendChild(timeTab);

    return tabs;
  }

  /**
   * Create footer
   * @private
   */
  _createFooter() {
    const footer = document.createElement("div");
    footer.className = "ndtp-footer";

    const nowButton = document.createElement("button");
    nowButton.className = "ndtp-btn ndtp-btn-now";
    nowButton.textContent = "Now";
    nowButton.type = "button";
    nowButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._selectNow();
    };

    const clearButton = document.createElement("button");
    clearButton.className = "ndtp-btn ndtp-btn-clear";
    clearButton.textContent = "Clear";
    clearButton.type = "button";
    clearButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.clear();
    };

    const okButton = document.createElement("button");
    okButton.className = "ndtp-btn ndtp-btn-ok";
    okButton.textContent = "OK";
    okButton.type = "button";
    okButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._handleOk();
    };

    footer.appendChild(nowButton);
    footer.appendChild(clearButton);
    footer.appendChild(okButton);

    return footer;
  }

  /**
   * Initialize date and time pickers
   * @private
   */
  _initializePickers() {
    const dateContainer = this.pickerContainer.querySelector(
      ".ndtp-date-container"
    );
    const timeContainer = this.pickerContainer.querySelector(
      ".ndtp-time-container"
    );

    // Initialize date picker
    this.datePicker = new DatePicker({
      calendar: this.options.calendar,
      language: this.options.language,
      format: this.options.dateFormat,
      minDate: this.options.minDate,
      maxDate: this.options.maxDate,
      disabledDates: this.options.disabledDates,
      disabledDaysOfWeek: this.options.disabledDaysOfWeek,
      closeOnSelect: false,
      showInput: false,
      inline: true,
      onChange: (date) => {
        this._updateValue();
        // Automatically switch to time tab after date selection
        if (date && this.activeTab === "date") {
          setTimeout(() => {
            this._switchTab("time");
          }, 300); // Small delay for better UX
        }
      },
    });
    this.datePicker.initialize(dateContainer);

    // Initialize time picker
    this.timePicker = new TimePicker({
      format: this.options.timeFormat,
      language: this.options.language,
      minTime: this.options.minTime,
      maxTime: this.options.maxTime,
      minuteStep: this.options.minuteStep,
      onChange: (time) => {
        this._updateValue();
      },
    });
    this.timePicker.initialize(timeContainer);
  }

  /**
   * Attach event listeners
   * @private
   */
  _attachEventListeners() {
    if (this.input && !this.options.inline) {
      // Show picker on input click
      this.input.addEventListener("click", (e) => {
        e.stopPropagation();
        this.show();
      });

      // Show picker on input focus
      this.input.addEventListener("focus", (e) => {
        e.stopPropagation();
        this.show();
      });
    }

    // Prevent clicks inside picker from closing it
    if (this.pickerContainer) {
      this.pickerContainer.addEventListener("click", (e) => {
        e.stopPropagation();
      });
    }

    // Close on outside click
    this._handleOutsideClick = (e) => {
      if (!this.options.inline && this.isVisible) {
        const target = e.target;
        if (
          !this.container.contains(target) &&
          !this.pickerContainer.contains(target)
        ) {
          this.hide();
        }
      }
    };
    document.addEventListener("click", this._handleOutsideClick);

    // Close on escape key
    this._handleKeyDown = (e) => {
      if (e.key === "Escape" && this.isVisible && !this.options.inline) {
        this.hide();
      }
    };
    document.addEventListener("keydown", this._handleKeyDown);
  }

  /**
   * Switch between date and time tabs
   * @private
   */
  _switchTab(tab) {
    if (this.activeTab === tab) return;

    const tabs = this.pickerContainer.querySelectorAll(".ndtp-tab");
    tabs.forEach((t) => t.classList.remove("active"));

    if (tab === "date") {
      tabs[0].classList.add("active");
    } else {
      tabs[1].classList.add("active");
    }

    this._updateTabVisibility(tab);
    this.activeTab = tab;
  }

  /**
   * Update tab visibility
   * @private
   */
  _updateTabVisibility(activeTab) {
    const dateContainer = this.pickerContainer.querySelector(
      ".ndtp-date-container"
    );
    const timeContainer = this.pickerContainer.querySelector(
      ".ndtp-time-container"
    );

    if (activeTab === "date") {
      dateContainer.style.display = "block";
      timeContainer.style.display = "none";
      timeContainer.classList.remove("active");
    } else {
      dateContainer.style.display = "none";
      timeContainer.style.display = "flex";
      timeContainer.classList.add("active");
    }
  }

  /**
   * Select current date and time
   * @private
   */
  _selectNow() {
    const now = new Date();

    if (this.options.calendar === "nepali") {
      // Convert to Nepali date
      this.datePicker.goToToday();
      this.datePicker.setDate(this.datePicker.calendar.getCurrentDate());
    } else {
      const currentDate = {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
      };
      this.datePicker.setDate(currentDate);
    }

    const currentTime = {
      hour: now.getHours(),
      minute: now.getMinutes(),
    };
    this.timePicker.setTime(currentTime);

    this._updateValue();
  }

  /**
   * Handle OK button click
   * @private
   */
  _handleOk() {
    this._updateValue();

    if (this.options.closeOnSelect && !this.options.inline) {
      this.hide();
    }
  }

  /**
   * Update input value
   * @private
   */
  _updateValue() {
    const date = this.datePicker.getDate();
    const time = this.timePicker.getTime();

    if (!date) return;

    // Set default time if not selected
    const finalTime = time || { hour: 0, minute: 0 };

    const formattedDate = this.datePicker.calendar.formatDate(
      date,
      this.options.dateFormat
    );
    const formattedTime = this._formatTime(finalTime);

    const formattedDateTime = this.options.format
      .replace("YYYY-MM-DD", formattedDate)
      .replace("HH:mm", formattedTime);

    if (this.input) {
      this.input.value = formattedDateTime;
    }

    if (this.options.onChange) {
      this.options.onChange({
        date,
        time: finalTime,
        formatted: formattedDateTime,
      });
    }
  }

  /**
   * Format time
   * @private
   */
  _formatTime(time) {
    let hour = time.hour;
    const minute = time.minute.toString().padStart(2, "0");
    let ampm = "";

    if (this.options.timeFormat === "12") {
      ampm = hour >= 12 ? " PM" : " AM";
      hour = hour % 12 || 12;
    }

    const formattedHour = hour.toString().padStart(2, "0");

    if (this.options.language === "ne") {
      return this._toNepaliDigits(`${formattedHour}:${minute}${ampm}`);
    }

    return `${formattedHour}:${minute}${ampm}`;
  }

  /**
   * Convert to Nepali digits
   * @private
   */
  _toNepaliDigits(text) {
    const nepaliDigits = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
    return String(text).replace(/\d/g, (digit) => nepaliDigits[digit]);
  }

  /**
   * Show the picker
   */
  show() {
    if (this.isVisible || this.options.inline) return;

    this.pickerContainer.style.display = "block";
    this.isVisible = true;

    // Position the picker
    this._positionPicker();

    if (this.options.onShow) {
      this.options.onShow();
    }
  }

  /**
   * Hide the picker
   */
  hide() {
    if (!this.isVisible || this.options.inline) return;

    this.pickerContainer.style.display = "none";
    this.isVisible = false;

    if (this.options.onHide) {
      this.options.onHide();
    }
  }

  /**
   * Position the picker
   * @private
   */
  _positionPicker() {
    if (this.options.inline || !this.input) return;

    const inputRect = this.input.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    // Position below input by default
    let top = inputRect.bottom + scrollTop;
    let left = inputRect.left + scrollLeft;

    // Check if picker would go off screen bottom
    const pickerHeight = 300; // Approximate height of picker
    if (top + pickerHeight > window.innerHeight + scrollTop) {
      // Position above input
      top = inputRect.top + scrollTop - pickerHeight;
    }

    // Check if picker would go off screen right
    const pickerWidth = 280; // Approximate width of picker
    if (left + pickerWidth > window.innerWidth + scrollLeft) {
      // Align to right edge of input
      left = inputRect.right + scrollLeft - pickerWidth;
    }

    this.pickerContainer.style.position = "absolute";
    this.pickerContainer.style.top = `${top}px`;
    this.pickerContainer.style.left = `${left}px`;
    this.pickerContainer.style.zIndex = "1000";

    // Ensure picker is added to document body for proper positioning
    document.body.appendChild(this.pickerContainer);
  }

  /**
   * Get selected datetime
   * @returns {Object|null} Selected datetime
   */
  getDateTime() {
    const date = this.datePicker.getDate();
    const time = this.timePicker.getTime();

    if (!date) return null;

    return {
      date,
      time: time || { hour: 0, minute: 0 },
    };
  }

  /**
   * Set datetime
   * @param {Object} datetime - Datetime object {date, time}
   */
  setDateTime(datetime) {
    if (!datetime) {
      throw new Error("Invalid datetime");
    }

    if (datetime.date) {
      this.datePicker.setDate(datetime.date);
    }

    if (datetime.time) {
      this.timePicker.setTime(datetime.time);
    }

    this._updateValue();
  }

  /**
   * Parse datetime string
   * @param {string} datetimeString - Datetime string
   * @returns {Object|null} Parsed datetime
   */
  parseDateTime(datetimeString) {
    if (!datetimeString) return null;

    // Extract date and time parts based on format
    const datePattern = this.options.dateFormat
      .replace("YYYY", "(\\d{4})")
      .replace("MM", "(\\d{2})")
      .replace("DD", "(\\d{2})");

    const timePattern = "HH:mm"
      .replace("HH", "(\\d{2})")
      .replace("mm", "(\\d{2})");

    const pattern = this.options.format
      .replace("YYYY-MM-DD", datePattern)
      .replace("HH:mm", timePattern);

    const regex = new RegExp(pattern);
    const match = datetimeString.match(regex);

    if (!match) return null;

    // Parse date
    let dateIndex = 1;
    const date = {
      year: parseInt(match[dateIndex++], 10),
      month: parseInt(match[dateIndex++], 10),
      day: parseInt(match[dateIndex++], 10),
    };

    // Parse time
    const time = {
      hour: parseInt(match[dateIndex++], 10),
      minute: parseInt(match[dateIndex], 10),
    };

    if (!this.datePicker.calendar.isValidDate(date)) {
      return null;
    }

    return { date, time };
  }

  /**
   * Clear selected datetime
   */
  clear() {
    this.datePicker.clear();
    this.timePicker.clear();

    if (this.input) {
      this.input.value = "";
    }

    if (this.options.onChange) {
      this.options.onChange(null);
    }
  }

  /**
   * Destroy the datetime picker
   */
  destroy() {
    if (this.datePicker) {
      this.datePicker.destroy();
    }

    if (this.timePicker) {
      this.timePicker.destroy();
    }

    if (this.container) {
      this.container.innerHTML = "";
      this.container.classList.remove("ndtp-datetime-picker");
    }

    // Remove event listeners
    if (this._handleOutsideClick) {
      document.removeEventListener("click", this._handleOutsideClick);
    }
    if (this._handleKeyDown) {
      document.removeEventListener("keydown", this._handleKeyDown);
    }

    this.initialized = false;
  }

  /**
   * Get current configuration
   * @returns {Object} Current options
   */
  getConfig() {
    return { ...this.options };
  }

  /**
   * Update configuration
   * @param {Object} options - New options
   */
  updateConfig(options) {
    this.options = { ...this.options, ...options };

    // Reinitialize with new options
    if (this.initialized) {
      const selectedDateTime = this.getDateTime();
      this.destroy();
      this.initialize(this.container);

      if (selectedDateTime) {
        this.setDateTime(selectedDateTime);
      }
    }
  }

  /**
   * Check if picker is visible
   * @returns {boolean}
   */
  isOpen() {
    return this.isVisible;
  }

  /**
   * Convert datetime between calendars
   * @param {Object} datetime - Datetime to convert
   * @param {string} toCalendar - Target calendar ('nepali' or 'english')
   * @returns {Object} Converted datetime
   */
  convertDateTime(datetime, toCalendar) {
    const convertedDate = this.datePicker.convertDate(
      datetime.date,
      toCalendar
    );
    return {
      date: convertedDate,
      time: datetime.time,
    };
  }
}

export default DateTimePicker;
